'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import cx from 'classnames';

import { Image } from '@/components/Default/Image';
import { Link } from '@/components/Default/Link';

import { Theme } from './API/Theme';

import Navigation from './Navigation';

interface NavbarProps {
    theme: Theme;
    distance: number;
    gradient?: boolean;
}

const Navbar = ({ theme, distance, gradient = true }: NavbarProps) => {
    const [hasTraveled, setHasTraveled] = useState<boolean>(false);
    const [currentHref, setHref] = useState<string|null>(null);

    useEffect(() => setHref(window.location.pathname), []);
    useEffect(() => setHasTraveled(distance > window.innerHeight), [distance]);

    const imageSrc = theme === 'light' || hasTraveled
        ? 'https://timeforsolar-assets.s3.eu-west-2.amazonaws.com/logo-letterform-02.svg'  // Black Text
        : 'https://timeforsolar-assets.s3.eu-west-2.amazonaws.com/logo-letterform-06.svg';   // White Text

    return (
        <nav className={cx(
            'top-0 z-[39] w-full hidden 2xl:flex justify-between items-center overflow-hidden',
            'duration-500', {
                'text-white absolute py-8': ! hasTraveled,
                'text-black fixed top-0 py-4 bg-white border-b shadow-sm': hasTraveled
            }
        )}>
            <div className={cx('absolute left-0 top-0 right-0 w-full h-16 z-30 bg-gradient-to-b from-black/50 to-transparent', {
                'hidden': hasTraveled && gradient || ! gradient
            })} />

            <Link href='/' className='px-16 py-4 shrink-0 -translate-y-[14%]' aria-label='Naar de hoofdpagina'>
                <Image src={imageSrc} width={300} height={0} alt='' />
            </Link>

            <ol className='flex font-medium px-16 text-lg gap-2 self-end'>
                {Navigation.map(({ title, href }, idx) => {
                    return (
                        <Link key={idx} href={href} className={cx('rounded-md', {
                            'hover:bg-black/5 text-black': theme === 'light' || hasTraveled,
                            'hover:bg-white/10 text-white': theme === 'dark' && ! hasTraveled
                        })}>
                            <motion.li className='relative cursor-pointer py-2 whitespace-nowrap' animate={true}>
                                {currentHref === href && <motion.div className='w-full h-1 absolute -bottom-4 bg-primary-500' />}
                                <span className='tracking-wide px-3 py-2'>{title}</span>
                            </motion.li>
                        </Link>
                    );
                })}
            </ol>
        </nav>
    );
};

export { Navbar };